/**
 * Service for storing and retrieving goal planning data from localStorage.
 * This will be replaced with backend API calls when ready.
 */

import type { GoalTemplateParams, GoalType } from '@/api/generated/model';

export interface RevenueStream {
  id: string;
  name: string;
  amount: number;
  frequency: 'monthly' | 'yearly';
  currency: string;
}

export interface FinancialInfo {
  salary: number;
  currency: string;
  revenueStreams: RevenueStream[];
  totalMonthlyIncome: number;
  totalYearlyIncome: number;
}

export interface PlannedGoal {
  id: string;
  name: string;
  description?: string;
  targetAmount: number;
  currentAmount: number;
  currency: string;
  dueDate: string;
  priority: 'mandatory' | 'nice-to-have';
  rank: number; // 1-based ranking
  monthlyAllocation: number; // How much to allocate per month
  yearlyAllocation: number; // How much to allocate per year
  goalType?: GoalType;
  templateParams?: GoalTemplateParams;
}

export interface GoalPlan {
  financialInfo?: FinancialInfo | null;
  goals: PlannedGoal[];
  interests?: string[];
  createdAt: string;
  updatedAt: string;
}

const STORAGE_KEYS = {
  FINANCIAL_INFO: 'goal_planning_financial_info',
  GOAL_PLAN: 'goal_planning_plan',
  INTERESTS: 'goal_planning_interests',
  COMPLETED: 'goal_planning_completed',
};

export const goalPlanningStorage = {
  /**
   * Save financial information
   */
  saveFinancialInfo(info: FinancialInfo): void {
    localStorage.setItem(STORAGE_KEYS.FINANCIAL_INFO, JSON.stringify(info));
  },

  /**
   * Get financial information
   */
  getFinancialInfo(): FinancialInfo | null {
    const data = localStorage.getItem(STORAGE_KEYS.FINANCIAL_INFO);
    if (!data) return null;
    try {
      return JSON.parse(data) as FinancialInfo;
    } catch {
      return null;
    }
  },

  /**
   * Save goal plan
   */
  saveGoalPlan(plan: GoalPlan): void {
    localStorage.setItem(STORAGE_KEYS.GOAL_PLAN, JSON.stringify(plan));
  },

  /**
   * Get goal plan
   */
  getGoalPlan(): GoalPlan | null {
    const data = localStorage.getItem(STORAGE_KEYS.GOAL_PLAN);
    if (!data) return null;
    try {
      return JSON.parse(data) as GoalPlan;
    } catch {
      return null;
    }
  },

  saveInterests(interests: string[]): void {
    localStorage.setItem(STORAGE_KEYS.INTERESTS, JSON.stringify(interests));
  },

  getInterests(): string[] {
    const data = localStorage.getItem(STORAGE_KEYS.INTERESTS);
    if (!data) return [];
    try {
      const parsed = JSON.parse(data) as unknown;
      return Array.isArray(parsed) ? (parsed as string[]) : [];
    } catch {
      return [];
    }
  },

  /**
   * Mark goal planning as completed
   */
  markCompleted(): void {
    localStorage.setItem(STORAGE_KEYS.COMPLETED, 'true');
  },

  /**
   * Check if goal planning is completed
   */
  isCompleted(): boolean {
    return localStorage.getItem(STORAGE_KEYS.COMPLETED) === 'true';
  },

  /**
   * Clear all goal planning data
   */
  clearAll(): void {
    localStorage.removeItem(STORAGE_KEYS.FINANCIAL_INFO);
    localStorage.removeItem(STORAGE_KEYS.GOAL_PLAN);
    localStorage.removeItem(STORAGE_KEYS.INTERESTS);
    localStorage.removeItem(STORAGE_KEYS.COMPLETED);
  },
};
